import { useState } from 'react';
import {
  formatFileSize,
  selfDisclosureFormRules,
  selfDisclosureProofTypeLabels,
  selfDisclosureProofTypes,
  type SelfDisclosureProofType,
} from '@brimo/shared';
import { Banner, FileField, Select, type FileFieldItem } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { removeProof, uploadProof, type PublicProof } from '../api';
import styles from './SelfDisclosurePublicPage.module.css';

export interface ProofsUploadProps {
  token: string;
  proofs: PublicProof[];
  /** Called after an upload or a removal (the flow refetches the state and notes the save). */
  onChanged: () => void | Promise<void>;
  onError: (error: unknown) => void;
}

/** Step 4 „Nachweise" (optional): type + file → `POST …/proofs`; the rows list the uploads with their scan state. */
export function ProofsUpload({ token, proofs, onChanged, onError }: ProofsUploadProps) {
  const [type, setType] = useState<SelfDisclosureProofType>('id_document');
  const [running, setRunning] = useState<FileFieldItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function upload(files: File[]) {
    setError(null);
    for (const file of files) {
      const item: FileFieldItem = { id: `upload-${Date.now()}-${file.name}`, name: file.name, progress: 30 };
      setRunning((list) => [...list, item]);
      try {
        await uploadProof(token, type, file);
        setRunning((list) => list.filter((entry) => entry.id !== item.id));
        await onChanged();
      } catch (cause) {
        setRunning((list) => list.filter((entry) => entry.id !== item.id));
        const message =
          cause instanceof ApiError
            ? (cause.fields['file'] ?? cause.fields['type'] ?? cause.message)
            : errorMessage(cause);
        setError(message);
        onError(cause);
      }
    }
  }

  async function remove(item: FileFieldItem) {
    setError(null);
    try {
      await removeProof(token, item.id);
      await onChanged();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  const items: FileFieldItem[] = [
    ...proofs.map((proof) => ({
      id: proof.id,
      name: `${proof.typeLabel} · ${proof.fileName}`,
      size: `${formatFileSize(proof.size)}${proof.scanStatus === 'pending' ? ' · wird geprüft' : ''}`,
    })),
    ...running,
  ];

  return (
    <div className={styles.proofs}>
      <div className={styles.proofsHead}>
        <span className={styles.proofsTitle}>Nachweise (optional)</span>
        <span className={styles.proofsText}>
          Personalausweis, Gehaltsnachweise und Eigenkapitalnachweis beschleunigen die Prüfung. PDF, JPG oder
          PNG bis {selfDisclosureFormRules.uploadMaxMb} MB.
        </span>
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <Select
        label="Art des Nachweises"
        value={type}
        className={styles.field}
        options={selfDisclosureProofTypes.map((value) => ({
          value,
          label: selfDisclosureProofTypeLabels[value],
        }))}
        onChange={(event) => setType(event.target.value as SelfDisclosureProofType)}
      />
      <FileField
        label="Datei"
        labelHidden
        title="Datei hierher ziehen"
        description="oder auswählen — PDF, JPG oder PNG."
        buttonLabel="Datei auswählen"
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        onFiles={(files) => void upload(files)}
        items={items}
        onRemove={(item) => void remove(item)}
      />
    </div>
  );
}
